import type { Address } from 'viem';

import { adapterAbi, courtOracleAbi, erc20Abi, factoryAbi, marketAbi } from '../shared/abi';
import { readClient } from '../shared/client';
import { DEPLOYMENT, getActiveChainConfig, getActiveNetworkKey, setActiveNetwork, type NetworkKey } from '../shared/config';
import { fmtDuration, fmtToken, shortAddr } from '../shared/format';
import { logLine } from '../shared/logger';
import { escapeHtml, sanitizeTokenSymbol } from '../shared/sanitize';
import {
  bindWalletEvents,
  connectWallet,
  ensurePulseChainConnected,
  rehydrateWalletSession,
  walletState,
} from '../shared/wallet';

type InboxItem = {
  marketAddress: Address;
  marketId: `0x${string}`;
  question: string;
  action: string;
  detail: string;
  timeoutDetail?: string;
  deadline: number;
  priority: number;
};

type TimeoutStatus = {
  exists: boolean;
  disputed: boolean;
  finalized: boolean;
  proposedAt: number;
  disputeDeadline: number;
  escalationDeadline: number;
  emergencyDeadline: number;
  disputeWindowOpen: boolean;
  escalationActive: boolean;
  emergencyWindowOpen: boolean;
  timeoutReady: boolean;
};

type AssetMeta = {
  decimals: number;
  symbol: string;
};

const REFRESH_ICON_SVG = `<img src="${import.meta.env.BASE_URL}images/refreshing.svg" alt="" aria-hidden="true"/>`;

function isZeroAddress(value: string): boolean {
  return /^0x0{40}$/i.test(value);
}

function fmtDeadline(ts: number): string {
  if (!ts) return 'n/a';
  const now = Math.floor(Date.now() / 1000);
  if (ts <= now) return `expired ${fmtDuration(now - ts)} ago`;
  return `${fmtDuration(ts - now)} remaining`;
}

export function mountJurorView(app: HTMLElement): void {
  const activeChain = getActiveChainConfig();
  const activeNetwork = getActiveNetworkKey();

  app.innerHTML = `
  <div class="app">
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark" aria-hidden="true"></div>
        <div>
          <div class="brand-text">Juror Inbox</div>
          <div class="brand-sub">${activeChain.chainName} · ${activeChain.id}</div>
        </div>
      </div>
      <div class="header-actions">
        <details class="header-dropdown" id="networkDropdown">
          <summary class="header-dropdown-btn" aria-label="Network selector">
            <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="9" fill="none" stroke-width="2"/>
              <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" fill="none" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="header-dropdown-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="header-dropdown-menu" role="menu" aria-label="Network selector">
            <button id="networkMainnetBtn" class="header-dropdown-item ${activeNetwork === 'mainnet' ? 'active' : ''}" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2v20M2 12h20" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                Mainnet
              </span>
            </button>
            <button id="networkTestnetBtn" class="header-dropdown-item ${activeNetwork === 'testnet' ? 'active' : ''}" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 6h16v12H4zM8 10h8" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Testnet
              </span>
            </button>
          </div>
        </details>
        <a href="./" class="btn header-back-btn" aria-label="Back to markets" title="Back to markets">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
        <details class="header-dropdown" id="linksDropdown">
          <summary class="header-dropdown-btn" aria-label="Header links">
            <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" fill="none" stroke-width="2" stroke-linejoin="round"/>
              <path d="M4 7.5l8 4.5 8-4.5M12 12v9" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="header-dropdown-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="header-dropdown-menu" role="menu" aria-label="Navigation links">
            <a href="/boxbet/help" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 18h.01M9.1 9a3 3 0 1 1 5.8 1c-.57.78-1.55 1.23-2.18 2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Help
              </span>
            </a>
            <a href="/boxbet/create/" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                Create Market
              </span>
            </a>
            <a href="/boxbet/adversary" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 4v5c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V7l8-4z" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Adversary
              </span>
            </a>
          </div>
        </details>
        <button id="connectBtn" class="wallet-pill">Connect Wallet</button>
      </div>
    </header>

    <section class="hero" style="padding:14px 16px;">
      <div class="hero-title" style="font-size:clamp(1rem,2.2vw,1.35rem);">Juror Action Queue</div>
      <div class="hero-sub">A single place to see where your vote is needed: commit, reveal, and appeal windows for active court rounds.</div>
    </section>

    <section class="card" style="margin-top:12px; padding:14px;">
      <div id="stakeSummary" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(180px, 1fr)); gap:10px; margin-bottom:12px;"></div>
      <div style="display:flex; justify-content:space-between; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:10px;">
        <div class="mono" style="font-size:.74rem; color:var(--muted)">Only actionable items are shown</div>
        <button id="refreshBtn" class="btn icon-btn" aria-label="Refresh inbox" title="Refresh Inbox">${REFRESH_ICON_SVG}</button>
      </div>
      <div id="inboxGrid" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:12px;"></div>
    </section>
  </div>
  `;

  const connectBtn = app.querySelector('#connectBtn') as HTMLButtonElement;
  const networkMainnetBtn = app.querySelector('#networkMainnetBtn') as HTMLButtonElement;
  const networkTestnetBtn = app.querySelector('#networkTestnetBtn') as HTMLButtonElement;
  const networkDropdown = app.querySelector('#networkDropdown') as HTMLDetailsElement;
  const linksDropdown = app.querySelector('#linksDropdown') as HTMLDetailsElement;
  const refreshBtn = app.querySelector('#refreshBtn') as HTMLButtonElement;
  const stakeSummary = app.querySelector('#stakeSummary') as HTMLElement;
  const inboxGrid = app.querySelector('#inboxGrid') as HTMLElement;
  const logsEl = document.createElement('div');

  function onNetworkToggle(next: NetworkKey) {
    if (next === getActiveNetworkKey()) return;
    setActiveNetwork(next);
    window.location.reload();
  }

  function positionHeaderDropdown(dropdown: HTMLDetailsElement | null): void {
    if (!dropdown || !dropdown.open) return;
    const summary = dropdown.querySelector('summary') as HTMLElement | null;
    const menu = dropdown.querySelector('.header-dropdown-menu') as HTMLElement | null;
    if (!summary || !menu) return;
    const viewportPadding = 8;
    const summaryRect = summary.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const centeredLeft = summaryRect.left + (summaryRect.width / 2) - (menuRect.width / 2);
    const maxLeft = Math.max(viewportPadding, window.innerWidth - viewportPadding - menuRect.width);
    const clampedLeft = Math.min(Math.max(viewportPadding, centeredLeft), maxLeft);
    menu.style.left = `${clampedLeft - summaryRect.left}px`;
    menu.style.right = 'auto';
    menu.style.transform = 'none';
  }

  function refreshHeaderDropdownPositions(): void {
    positionHeaderDropdown(networkDropdown);
    positionHeaderDropdown(linksDropdown);
  }

  const scheduleHeaderDropdownRefresh = (): void => {
    window.requestAnimationFrame(refreshHeaderDropdownPositions);
  };

  networkDropdown.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  linksDropdown.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  window.addEventListener('resize', refreshHeaderDropdownPositions);

  networkMainnetBtn.addEventListener('click', () => onNetworkToggle('mainnet'));
  networkTestnetBtn.addEventListener('click', () => onNetworkToggle('testnet'));

  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    if (!networkDropdown.contains(target)) networkDropdown.open = false;
    if (!linksDropdown.contains(target)) linksDropdown.open = false;
  });

  function setAccountLabel(): void {
    connectBtn.textContent = walletState.account ? shortAddr(walletState.account) : 'Connect Wallet';
    connectBtn.classList.toggle('connected', Boolean(walletState.account));
  }

  async function getOracleAddress(): Promise<Address> {
    try {
      const addr = await readClient.readContract({
        address: DEPLOYMENT.adapter as Address,
        abi: adapterAbi,
        functionName: 'escalationOracle',
      });
      return (addr as Address) || (DEPLOYMENT.oracle as Address);
    } catch {
      return DEPLOYMENT.oracle as Address;
    }
  }

  function fmtDeadlineLabel(ts: number): string {
    if (!ts) return 'n/a';
    const now = Math.floor(Date.now() / 1000);
    if (ts <= now) return `expired ${fmtDuration(now - ts)} ago`;
    return `${fmtDuration(ts - now)} remaining`;
  }

  function timeoutDetail(status: TimeoutStatus): string {
    if (!status.exists || status.finalized || !status.disputed) return 'No active escalation timeout.';
    if (status.disputeWindowOpen) return `Dispute window closes ${fmtDeadlineLabel(status.disputeDeadline)}.`;
    if (status.escalationActive) return `Court escalation window closes ${fmtDeadlineLabel(status.escalationDeadline)}.`;
    if (status.emergencyWindowOpen) return `Grand judge / emergency window closes ${fmtDeadlineLabel(status.emergencyDeadline)}.`;
    if (status.timeoutReady) return 'Hard timeout reached. Final disputed fallback settles Invalid.';
    return 'Timeout status unavailable.';
  }

  function renderStakeCard(label: string, value: string, note: string): string {
    return `
      <div class="market-card" style="padding:12px; min-height:auto;">
          <div class="mono tiny" style="color:var(--muted); margin-bottom:6px;">${escapeHtml(label)}</div>
          <div class="q" style="font-size:1rem; margin-bottom:4px;">${escapeHtml(value)}</div>
          <div class="mono tiny" style="color:rgba(126,165,192,.75)">${escapeHtml(note)}</div>
      </div>
    `;
  }

  async function loadStakeSummary(oracle: Address, account: Address): Promise<void> {
    const [jurorStatusRaw, stakeAssetRaw, appealAssetRaw] = await Promise.all([
      readClient.readContract({
        address: oracle,
        abi: courtOracleAbi,
        functionName: 'getJurorStatus',
        args: [account],
      }),
      readClient.readContract({ address: oracle, abi: courtOracleAbi, functionName: 'stakeAsset' }),
      readClient.readContract({ address: oracle, abi: courtOracleAbi, functionName: 'appealBondAsset' }),
    ]);

    const jurorStatus = jurorStatusRaw as [bigint, bigint, bigint, bigint, boolean, boolean, boolean];
    const stakeAsset = stakeAssetRaw as Address;
    const appealAsset = appealAssetRaw as Address;

    const [stakeDecimalsRaw, stakeSymbolRaw, appealDecimalsRaw, appealSymbolRaw] = await Promise.all([
      readClient.readContract({ address: stakeAsset, abi: erc20Abi, functionName: 'decimals' }),
      readClient.readContract({ address: stakeAsset, abi: erc20Abi, functionName: 'symbol' }),
      readClient.readContract({ address: appealAsset, abi: erc20Abi, functionName: 'decimals' }),
      readClient.readContract({ address: appealAsset, abi: erc20Abi, functionName: 'symbol' }),
    ]);

    const stakeMeta: AssetMeta = {
      decimals: Number(stakeDecimalsRaw as number | bigint),
      symbol: sanitizeTokenSymbol(stakeSymbolRaw, 'TOKEN'),
    };
    const appealMeta: AssetMeta = {
      decimals: Number(appealDecimalsRaw as number | bigint),
      symbol: sanitizeTokenSymbol(appealSymbolRaw, 'TOKEN'),
    };

    const [staked, pendingStakeReward, pendingAppealReward, assignments, isActive, meetsMinimumStake, canUnstake] = jurorStatus;

    stakeSummary.innerHTML = [
      renderStakeCard('Staked', `${fmtToken(staked, stakeMeta.decimals)} ${stakeMeta.symbol}`, isActive ? 'Active juror' : 'Inactive'),
      renderStakeCard('Assignments', `${assignments.toString()}`, canUnstake ? 'Eligible to unstake' : 'Unstake blocked by active case'),
      renderStakeCard('Stake Rewards', `${fmtToken(pendingStakeReward, stakeMeta.decimals)} ${stakeMeta.symbol}`, meetsMinimumStake ? 'Meets minimum stake' : 'Below minimum stake'),
      renderStakeCard('Appeal Rewards', `${fmtToken(pendingAppealReward, appealMeta.decimals)} ${appealMeta.symbol}`, 'Claimable after finalized appeals'),
    ].join('');
  }

  async function loadInbox(): Promise<void> {
    if (!walletState.account) {
      stakeSummary.innerHTML = '';
      inboxGrid.innerHTML = '<div class="mono" style="color:var(--muted)">Connect wallet to load your juror inbox.</div>';
      return;
    }

    stakeSummary.innerHTML = '<div class="mono" style="color:var(--muted)">Loading juror stake status...</div>';
    inboxGrid.innerHTML = '<div class="mono" style="color:var(--muted)">Loading juror inbox...</div>';

    const account = walletState.account as Address;
    const oracle = await getOracleAddress();
    await loadStakeSummary(oracle, account);

    const marketCountRaw = await readClient.readContract({
      address: DEPLOYMENT.factory as Address,
      abi: factoryAbi,
      functionName: 'marketCount',
    });
    const marketCount = Number(marketCountRaw as bigint);

    if (marketCount === 0) {
      inboxGrid.innerHTML = '<div class="mono" style="color:var(--muted)">No markets found.</div>';
      return;
    }

    const indices = Array.from({ length: marketCount }, (_, i) => BigInt(i));
    const marketAddresses = await Promise.all(indices.map((i) => readClient.readContract({
      address: DEPLOYMENT.factory as Address,
      abi: factoryAbi,
      functionName: 'markets',
      args: [i],
    }) as Promise<Address>));

    const items: InboxItem[] = [];

    for (const marketAddress of marketAddresses) {
      try {
        const [marketIdRaw, questionRaw] = await Promise.all([
          readClient.readContract({
            address: marketAddress,
            abi: marketAbi,
            functionName: 'marketId',
          }),
          readClient.readContract({
            address: marketAddress,
            abi: marketAbi,
            functionName: 'question',
          }),
        ]);

        const marketId = marketIdRaw as `0x${string}`;
        const question = String(questionRaw || 'Untitled market');

        const [courtCaseRaw, timingRaw, participationRaw, timeoutStatusRaw] = await Promise.all([
          readClient.readContract({
            address: oracle,
            abi: courtOracleAbi,
            functionName: 'courtCases',
            args: [marketId],
          }),
          readClient.readContract({
            address: oracle,
            abi: courtOracleAbi,
            functionName: 'getJurorCaseTiming',
            args: [marketId],
          }),
          readClient.readContract({
            address: oracle,
            abi: courtOracleAbi,
            functionName: 'getJurorCaseParticipation',
            args: [marketId, account],
          }),
          readClient.readContract({
            address: DEPLOYMENT.adapter as Address,
            abi: adapterAbi,
            functionName: 'getDisputeTimeoutStatus',
            args: [marketId],
          }),
        ]);

        const courtCase = courtCaseRaw as [
          boolean,
          boolean,
          `0x${string}`,
          number,
          number,
          Address,
          Address,
          number,
          number,
          bigint,
          bigint,
        ];

        const caseExists = Boolean(courtCase[0]);
        const caseResolved = Boolean(courtCase[1]);
        const proposer = String(courtCase[5] || '');
        const disputer = String(courtCase[6] || '');
        const appealDeadline = Number(courtCase[9] || 0n);

        if (!caseExists || caseResolved) continue;

        const timing = timingRaw as [boolean, boolean, bigint, boolean, boolean, boolean, bigint, bigint, bigint];
        const participation = participationRaw as [boolean, boolean, boolean, number, boolean, boolean, boolean];
        const timeoutStatusTuple = timeoutStatusRaw as [boolean, boolean, boolean, bigint, bigint, bigint, bigint, boolean, boolean, boolean, boolean];
        const timeoutStatus: TimeoutStatus = {
          exists: Boolean(timeoutStatusTuple[0]),
          disputed: Boolean(timeoutStatusTuple[1]),
          finalized: Boolean(timeoutStatusTuple[2]),
          proposedAt: Number(timeoutStatusTuple[3] || 0n),
          disputeDeadline: Number(timeoutStatusTuple[4] || 0n),
          escalationDeadline: Number(timeoutStatusTuple[5] || 0n),
          emergencyDeadline: Number(timeoutStatusTuple[6] || 0n),
          disputeWindowOpen: Boolean(timeoutStatusTuple[7]),
          escalationActive: Boolean(timeoutStatusTuple[8]),
          emergencyWindowOpen: Boolean(timeoutStatusTuple[9]),
          timeoutReady: Boolean(timeoutStatusTuple[10]),
        };

        const roundNumber = Number(timing[2]);
        const roundResolved = Boolean(timing[3]);
        const appealFiled = Boolean(timing[4]);
        const timeoutShortCircuited = Boolean(timing[5]);
        const commitDeadline = Number(timing[6] || 0n);
        const revealDeadline = Number(timing[7] || 0n);
        const isSelected = Boolean(participation[0]);
        const committed = Boolean(participation[1]);
        const revealed = Boolean(participation[2]);
        const commitOpen = Boolean(participation[4]);
        const revealOpen = Boolean(participation[5]);
        const appealOpen = Boolean(participation[6]);

        const now = Math.floor(Date.now() / 1000);
        const timeoutText = timeoutDetail(timeoutStatus);

        if (isSelected && commitOpen && !committed) {
          items.push({
            marketAddress,
            marketId,
            question,
            action: 'Commit vote',
            detail: `Round ${roundNumber} is open for commit.`,
            timeoutDetail: timeoutText,
            deadline: commitDeadline,
            priority: 1,
          });
          continue;
        }

        if (isSelected && revealOpen && committed && !revealed) {
          items.push({
            marketAddress,
            marketId,
            question,
            action: 'Reveal vote',
            detail: `Round ${roundNumber} commit is closed. Reveal window is open.`,
            timeoutDetail: timeoutText,
            deadline: revealDeadline,
            priority: 0,
          });
          continue;
        }

        const hasProposer = proposer && !isZeroAddress(proposer);
        const isProposer = hasProposer && account.toLowerCase() === proposer.toLowerCase();
        const isDisputer = Boolean(disputer && !isZeroAddress(disputer) && account.toLowerCase() === disputer.toLowerCase());
        const userIsParty = isProposer || isDisputer;

        if (roundResolved && !appealFiled && userIsParty && appealOpen && now < appealDeadline) {
          items.push({
            marketAddress,
            marketId,
            question,
            action: 'Appeal available',
            detail: `Round ${roundNumber} resolved. Appeal window is open for case parties.`,
            timeoutDetail: timeoutText,
            deadline: appealDeadline,
            priority: 2,
          });
          continue;
        }

        if ((isSelected || userIsParty) && !roundResolved) {
          items.push({
            marketAddress,
            marketId,
            question,
            action: timeoutShortCircuited ? 'Round short-circuited' : 'Awaiting timeout',
            detail: timeoutShortCircuited
              ? `Round ${roundNumber} was short-circuited before reveal closed. Stake is preserved while escalation continues.`
              : `No juror action pending right now. Monitor the current timeout window.`,
            timeoutDetail: timeoutText,
            deadline: timeoutStatus.emergencyWindowOpen
              ? timeoutStatus.emergencyDeadline
              : timeoutStatus.escalationActive
                ? timeoutStatus.escalationDeadline
                : timeoutStatus.disputeWindowOpen
                  ? timeoutStatus.disputeDeadline
                  : revealDeadline,
            priority: 3,
          });
        }
      } catch {
        // Skip unreadable market/case entries.
      }
    }

    if (!items.length) {
      inboxGrid.innerHTML = '<div class="mono" style="color:var(--muted)">No active juror actions for this wallet.</div>';
      return;
    }

    items.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.deadline - b.deadline;
    });

    inboxGrid.innerHTML = items.map((item) => `
      <a class="market-card" href="?market=${item.marketAddress}&tab=courtPanel">
        <div class="chips" style="margin-bottom:8px;">
          <span class="meta-chip resolving">${escapeHtml(item.action)}</span>
          <span class="meta-chip">${fmtDeadline(item.deadline)}</span>
        </div>
        <div class="q">${escapeHtml(item.question)}</div>
        <div class="mono tiny" style="margin-top:8px; color:var(--muted)">${escapeHtml(item.detail)}</div>
        ${item.timeoutDetail ? `<div class="mono tiny" style="margin-top:6px; color:rgba(255,208,128,.85)">${escapeHtml(item.timeoutDetail)}</div>` : ''}
        <div class="mono tiny" style="margin-top:8px; color:rgba(126,165,192,.75)">${shortAddr(item.marketAddress)} · ${shortAddr(item.marketId)}</div>
      </a>
    `).join('');
  }

  connectBtn.addEventListener('click', async () => {
    try {
      await connectWallet();
      const chainOk = await ensurePulseChainConnected();
      setAccountLabel();
      if (!chainOk) {
        logLine(logsEl, 'warn', `Switch to ${getActiveChainConfig().chainName} to continue`);
        return;
      }
      await loadInbox();
      logLine(logsEl, 'ok', `Inbox loaded for ${walletState.account}`);
    } catch (error: any) {
      logLine(logsEl, 'err', error?.message || 'Wallet connect failed');
    }
  });

  refreshBtn.addEventListener('click', async () => {
    refreshBtn.classList.remove('is-spinning');
    void refreshBtn.offsetWidth;
    refreshBtn.classList.add('is-spinning');

    try {
      await loadInbox();
      logLine(logsEl, 'ok', 'Juror inbox refreshed');
    } catch (error: any) {
      logLine(logsEl, 'err', error?.message || 'Refresh failed');
    }
  });

  refreshBtn.addEventListener('animationend', (event) => {
    if (event.animationName !== 'refresh-spin-once') return;
    refreshBtn.classList.remove('is-spinning');
  });

  bindWalletEvents(async () => {
    setAccountLabel();
    if (walletState.account) {
      await ensurePulseChainConnected();
      await loadInbox();
    }
  });

  void rehydrateWalletSession().then(async () => {
    setAccountLabel();
    if (walletState.account) {
      await ensurePulseChainConnected();
      await loadInbox();
    }
  }).catch(() => {
    // no-op
  });

  setAccountLabel();
}
